import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableFooter } from './ReportsGroupTable.styles';
import { useFilters } from '../../hooks/useFilters';
import { IGroupOfReportsItem } from '../../services/reports';
import { IPagingInfo } from '../../services/surveys';
import GoToPage from '../GoToPage/GoToPage';
import NoData from '../NoData/NoData';
import Pagination from '../Pagination/Pagination';
import RowsPerPage from '../RowsPerPage/RowsPerPage';
import ReportsGroupTableTile from './ReportsGroupTableTile/ReportsGroupTableTile';

interface IReportsGroupTable {
  items: IGroupOfReportsItem[];
  metadata: IPagingInfo;
}

const ReportsGroupTable: FC<IReportsGroupTable> = ({ items, metadata }) => {
  const { t } = useTranslation();
  const {
    applyFilters,
    applyManyNestedFilters,
    applyNestedFilter,
    currentFilters,
    getNestedFilters,
  } = useFilters();
  const reportsKey = 'reports';

  const applyActiveFilter = () => {
    const filterArray = items.map(item => {
      if (item.reports && item.reports.items.length > 0) {
        return { id: item.surveyId, filter: { active: true } };
      } else {
        return { id: item.surveyId, filter: { active: false } };
      }
    });
    applyManyNestedFilters(filterArray, reportsKey);
  };

  useEffect(() => {
    if (currentFilters.search) {
      applyActiveFilter();
    } else if (items.length > 0 && Object.keys(getNestedFilters(reportsKey)).length === 0) {
      applyNestedFilter({ active: true }, items[0].surveyId, reportsKey);
    }
  }, [items]);

  const renderTableBody = useMemo(() => {
    return items.map((item: IGroupOfReportsItem, index) => (
      <ReportsGroupTableTile key={index} item={item} />
    ));
  }, [items]);

  return (
    <>
      {items.length > 0 ? renderTableBody : <NoData message={t('reports.noReportsInfo')} />}
      <TableFooter id="TableFooter">
        <RowsPerPage
          itemsPerPage={Number(metadata.itemsPerPage)}
          pagesCount={Number(metadata.totalPageCount)}
          menu={['7', '15', '25', '50', '75', '100']}
        />
        <Pagination
          activePage={Number(metadata.pageNo)}
          totalPageCount={Number(metadata.totalPageCount)}
          applyFilters={(pageNo: number) => applyFilters({ pageNo: String(pageNo) })}
        />
        <GoToPage applyFilters={applyFilters} pageLimit={metadata.totalPageCount} />
      </TableFooter>
    </>
  );
};

export default ReportsGroupTable;
